import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  input,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { MatTooltip } from '@angular/material/tooltip'
import {
  ElderAuditedEntityComponent,
  ElderBasicPaneLayoutComponent,
  ElderDialogService,
  ElderDimensionsInputComponent,
  ElderHeaderComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneSubtitleComponent,
  ElderQuantityInputControlComponent,
  ElderSelectComponent,
  ElderToastService,
  ElderToolbarColumnDirective,
  ElderToolbarComponent,
  ElderToolbarService,
  ElderToolbarTitleService,
  ElderValidationErrorDirective,
  SelectionModelPopupDirective,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { EntityLinkComponent } from '../../entitylinks/entity-link/entity-link.component'
import { OutputPreviewService } from '../../output/preview/output-preview.service'
import { ReportSpec } from '../../output/report/report-job'
import { Package, PackagingItem } from '../package'
import { PackagePatch, PackagingItemPatchDto } from '../package-patch'
import { PackageStateSelectDirective } from '../package-state-select/package-state-select.directive'
import { PackageStorageLocationComponent } from '../package-storage-location/package-storage-location.component'
import { PackageService } from '../package.service'
import { PackagingGoodsComponent } from '../packaging-goods/packaging-goods.component'
import { PackagingMaterialTableComponent } from '../packaging-material-table/packaging-material-table.component'
import { PackageTypeSelectDirective } from '../types/package-type-select/package-type-select.directive'

@Component({
  selector: 'app-package-detail',
  templateUrl: './package-detail.component.html',
  styleUrls: ['./package-detail.component.scss'],
  imports: [
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneContentComponent,
    ElderPaneSubtitleComponent,
    MatIconButton,
    MatIcon,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    ElderSelectComponent,
    PackageTypeSelectDirective,
    PackageStateSelectDirective,
    ElderQuantityInputControlComponent,
    MatError,
    ElderValidationErrorDirective,
    ElderDimensionsInputComponent,
    ElderAuditedEntityComponent,
    MatTabGroup,
    MatTab,
    MatTooltip,
    PackagingMaterialTableComponent,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderToolbarComponent,
    ElderToolbarColumnDirective,
    EntityLinkComponent,
    PackageStorageLocationComponent,
    PackagingGoodsComponent,
  ],
  providers: [ElderToolbarService, ElderToolbarTitleService],
})
export class PackageDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/
  public readonly company = input.required<string>()

  private readonly _package$: BehaviorSubject<Package> = new BehaviorSubject(null)
  private packagePatch: PackagePatch

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  @ViewChild(SelectionModelPopupDirective)
  public selectionPopup: SelectionModelPopupDirective

  private packageService = inject(PackageService)
  private toastService = inject(ElderToastService)
  private dialogService = inject(ElderDialogService)
  private outputPreviewService = inject(OutputPreviewService)

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get package$(): Observable<Package> {
    return this._package$.asObservable()
  }

  public get package(): Package {
    return this._package$.getValue()
  }

  @Input()
  public set package(pkg: Package) {
    this._package$.next(pkg)
    this.packagePatch = new PackagePatch(pkg.id)
  }

  public get deletionEnabled(): boolean {
    return true
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public onPreviewPackageLabel(event?: Event): void {
    this.outputPreviewService.openPreviewDialog(ReportSpec.packageLabels(this.package.id))
  }

  public onPackagingItemsChange(items: PackagingItem[]) {
    this.packagePatch.packagingItems = items.map(
      (item) => new PackagingItemPatchDto(item.material.materialId, item.material.quantity)
    )
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    PackagePatch.applyPackageData(this.packagePatch, this.package)

    this.packageService.updateByPatch(this.packagePatch).subscribe({
      next: (pkg) => {
        this.package = pkg
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.dialogService
      .showConfirm({
        title: 'messages.deleting_confirmation_title',
        message: 'packages.confirmDeletionMsg',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.packageService.delete(this.package).subscribe({
            next: () => this.close.emit(),
            error: (err) => this.toastService.error('messages.deleting_failed', err),
          })
        }
      })
  }

  private emitClose(event: Event): void {
    this.close.emit(event)
  }
}
